import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import {
  ACCELERATED_ANIMATION_DURATION,
  ACCELERATED_EXIT_DURATION,
  ENTER_ANIMATION_DURATION,
  EXIT_ANIMATION_DURATION,
  EXIT_DURATION,
  ToastHandle,
} from './Toast.common';

type Options = {
  ref: React.Ref<ToastHandle>;
  autoHideDuration: number;
  /** Freezes the auto-hide timer, keeping whatever time is left. */
  isPaused: boolean;
  /** Restarts the auto-hide timer whenever it changes. */
  timerEpoch: number;
  onClose?: () => void;
  onExitStart?: () => void;
  /** For a platform driving its animation imperatively. Must be referentially stable. */
  onExit?: (animationDuration: number) => void;
};

type Result = {
  /** False until mounted, and again from the front of the exit. */
  isShown: boolean;
  /** The slide's length: the entry's until the exit starts, then the exit's. */
  animationDuration: number;
  /** For Banner's ✕, which calls `onClose` with a press event that must not be read as options. */
  handleDismissPress: () => void;
};

/**
 * A toast's lifecycle from mount to `onClose`, shared by both platforms: the auto-hide timer, the
 * exit window the slide-out runs in, and the `dismiss` handle a stack reaches in through.
 */
const useToastDismissal = ({
  ref,
  autoHideDuration,
  isPaused,
  timerEpoch,
  onClose,
  onExitStart,
  onExit,
}: Options): Result => {
  const [isShown, setIsShown] = useState(false);
  const [animationDuration, setAnimationDuration] = useState(ENTER_ANIMATION_DURATION);
  const exitTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Carried across pauses, so a resume picks up where it stopped.
  const remainingRef = useRef(autoHideDuration);

  // Both auto-hide and a manual dismiss land here: the held timer drops the second.
  const dismiss = useCallback(
    ({ accelerated = false }: { accelerated?: boolean } = {}): void => {
      if (exitTimeout.current) return;

      onExitStart?.();

      const duration = accelerated ? ACCELERATED_ANIMATION_DURATION : EXIT_ANIMATION_DURATION;
      setAnimationDuration(duration);
      setIsShown(false);
      onExit?.(duration);

      exitTimeout.current = setTimeout(
        () => {
          onClose?.();
        },
        accelerated ? ACCELERATED_EXIT_DURATION : EXIT_DURATION
      );
    },
    [onClose, onExitStart, onExit]
  );

  const handleDismissPress = useCallback((): void => dismiss(), [dismiss]);

  useImperativeHandle(ref, () => ({ dismiss }), [dismiss]);

  useEffect(() => {
    setIsShown(true);
  }, []);

  // Ordered before the timer effect, so a restart sees the full duration.
  useEffect(() => {
    remainingRef.current = autoHideDuration;
  }, [autoHideDuration, timerEpoch]);

  // Banks what is left on the way out, so a pause never restarts the countdown from the top.
  useEffect(() => {
    if (isPaused) return undefined;

    const startedAt = Date.now();
    const timeout = setTimeout(() => {
      dismiss();
    }, remainingRef.current);

    return () => {
      clearTimeout(timeout);
      remainingRef.current = Math.max(0, remainingRef.current - (Date.now() - startedAt));
    };
  }, [isPaused, timerEpoch, autoHideDuration, dismiss]);

  useEffect(
    () => () => {
      clearTimeout(exitTimeout.current);
    },
    []
  );

  return { isShown, animationDuration, handleDismissPress };
};

export default useToastDismissal;
